import { useEffect, useRef } from "react";
import classNames from "classnames";
import { useHotkeys } from "react-hotkeys-hook";
import { t } from "@/i18n-js/instance";
import { useElectronNotification } from "@/react/hooks/notifications/useElectronNotification";
import { useChatNotification } from "@circle-react/hooks/chatsV2";
import { TippyV2 } from "@circle-react-shared/TippyV2";
import { IconButton } from "../../IconButton";
import { BadgeIndicator } from "../BadgeIndicator";

export interface DMsPopoverButtonProps {
  isOpen?: boolean;
}

export const DMsPopoverButton = ({ isOpen = false }: DMsPopoverButtonProps) => {
  const directMessagesButtonRef = useRef<HTMLButtonElement>(null);
  const { unreadChatCount } = useChatNotification();
  const { setDesktopAppUnreadMessageCount } = useElectronNotification();

  useEffect(() => {
    setDesktopAppUnreadMessageCount(unreadChatCount);
  }, [unreadChatCount]);

  useHotkeys(
    "alt+m",
    () => {
      directMessagesButtonRef.current?.click();
    },
    [directMessagesButtonRef],
  );

  return (
    <div className="relative">
      <TippyV2 interactive={false} content={t("header_v3.direct_messages")}>
        <IconButton
          name="20-message-v3"
          buttonSize={36}
          iconSize={20}
          className={classNames(
            "hover:!bg-c-header-hover focus:!bg-c-header-hover !text-c-header hover:!text-c-header-active focus:!text-c-header-active !rounded-md p-1.5 duration-150",
            {
              "!bg-c-header-active !text-c-header-active": isOpen,
            },
          )}
          iconClassName={classNames("!text-inherit !transition-none")}
          ariaLabel={t("header_v3.direct_messages")}
          dataTestId="direct-messages-popover-button"
          ref={directMessagesButtonRef}
        />
        {unreadChatCount > 0 && (
          <BadgeIndicator
            count={unreadChatCount}
            dataTestId="unread_direct_messages_count"
          />
        )}
      </TippyV2>
    </div>
  );
};
